import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { ProductRewardsResponseDTO } from 'src/app/shared/models/dtos/responses/product-rewards-response-dto/ProductRewardsResponseDto';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';

@Component({
  selector: 'app-reward-actions',
  templateUrl: './reward-actions.component.html',
  styleUrls: ['./reward-actions.component.scss'],
})

/**
 * Reward actions
 */
export class RewardActionsComponent implements OnInit {
  @Input() reward!: RewardResponseDTO | ProductRewardsResponseDTO;
  @Output() editEvent = new EventEmitter<boolean>();
  @Output() deleteEvent = new EventEmitter<boolean>();
  activeLabel = 'active-status';
  activeMessageLabel = 'active-message';
  inactiveLabel = 'inactive-status';
  inactiveMessageLabel = 'inactive-message';
  hasRewardStarted = false;
  isLoading = false;
  language = '';

  /**
   * Constructor
   */
  constructor(
    private readonly translocoService: TranslocoService,
    private readonly snackBarService: SnackBarService,
    private readonly rewardService: RewardService,
    readonly authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * on init
   */
  ngOnInit(): void {
    this.language = this.translocoService.getActiveLang();
    if (this.reward.startDate) {
      const rewardDate = new Date(this.reward.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      rewardDate.setHours(0, 0, 0, 0);

      this.hasRewardStarted = rewardDate.getTime() <= today.getTime();
    } else {
      this.hasRewardStarted = false;
    }
  }

  /**
   * Gets the tooltip text for the reward based on its active status.
   * @return {string} Translated tooltip text for the reward.
   */
  get rewardStatusTooltip(): string {
    const label = this.reward.enabled ? this.activeLabel : this.inactiveLabel;
    const message = this.reward.enabled
      ? this.activeMessageLabel
      : this.inactiveMessageLabel;

    return `${this.translocoService.translate(
      label
    )}\n\r${this.translocoService.translate(message)}`;
  }

  /**
   * Emits an event on button click
   * @param {EventEmitter} event
   * @param {boolean} bol
   */
  onButtonClick(event: EventEmitter<boolean>, bol?: boolean): void {
    event.emit(bol);
  }

  /**
   * Toggles the active status of the reward.
   * @param {boolean} newStatus - The new status to set for the reward.
   */
  changeStatus(newStatus: boolean): void {
    if (this.isLoading) {
      return;
    }
    const previousStatus = this.reward.enabled;
    this.isLoading = true;
    this.reward.enabled = newStatus;

    if (!newStatus) {
      this.rewardService.disableReward(this.reward.id).subscribe({
        next: (res) => {
          this.reward.enabled = res.enabled;
          this.isLoading = false;
        },
        error: () => {
          this.reward.enabled = previousStatus;
          this.isLoading = false;
          this.snackBarService.error(
            'Failed to update reward status. Please try again.'
          );
        },
      });
    }

    if (newStatus) {
      this.rewardService.enableReward(this.reward.id).subscribe({
        next: (res) => {
          this.reward.enabled = res.enabled;
          this.isLoading = false;
        },
        error: (err) => {
          this.reward.enabled = previousStatus;
          this.isLoading = false;
          this.snackBarService.error(err.error.errorMessage);
        },
      });
    }
  }
}
